/**
 * WebRTC 媒体层：与服务端 SFU 协商上行/下行两条 PeerConnection。
 *
 * 替代老系统 AudioLib（CELP/GSM/G711）+ VideoLib（XviD）那套自研编解码 ——
 * 编解码交给浏览器（Opus / VP8 / H.264），这里只管协商与轨道管理。
 */

import { startSpeakMeter, type SpeakMeter } from './speakMeter'
import { applyPreferred } from './devices'
import { MsgType, type ICEServerConf, type MediaKind, type RTCKind } from './messages'
import type { Client } from './client'

/**
 * 检查浏览器是否允许使用摄像头与麦克风。
 *
 * 浏览器只在**安全上下文**下开放媒体设备：HTTPS，或者 localhost。
 * 用 http:// 加 IP 访问时，`navigator.mediaDevices` 直接是 undefined ——
 * 不是权限被拒，是整个 API 都不存在。
 *
 * 这一条在本机开发时永远碰不到（127.0.0.1 是豁免的），一部署到内网
 * 服务器就全线失效：进得了教室、白板聊天都正常，唯独音视频用不了，
 * 而点按钮只会抛一个 "Cannot read properties of undefined"，
 * 用户看到的是「点了没反应」。
 *
 * 局域网会议节点**不**属于这种情况：它走自签 HTTPS，参会者点过一次证书警告后
 * 安全上下文成立、能正常发言（2026-09-16 实测）。会落到这里的是「只配了 http 的
 * 内网部署」—— 那时收得到别人的声音画面、只是自己发不出，所以提示要说清「能看能听」，
 * 别让人以为整个会都进不了。
 */
export function mediaBlockedReason(): string | null {
  if (typeof navigator !== 'undefined' && navigator.mediaDevices) return null
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return '浏览器只在 HTTPS 下开放摄像头与麦克风。当前是 http 访问：' +
      '能看能听，但开不了麦和摄像头 —— 服务器配置 HTTPS 后才能发言。'
  }
  return '这个浏览器不支持摄像头与麦克风。'
}

/** 上行 offer 发出后等 answer 的上限，超过就不再等、允许下一轮协商。 */
const PUB_ANSWER_TIMEOUT_MS = 8000

/** 一条收到的远端轨。 */
export interface RemoteTrack {
  userId: number
  kind: MediaKind
  trackId: string
  stream: MediaStream
}

export interface MediaOptions {
  client: Client
  iceServers: ICEServerConf[]
}

type RemoteHandler = (t: RemoteTrack) => void
type RemoteGoneHandler = (trackId: string) => void

/**
 * MediaEngine 管理本端的推流与订阅。
 *
 * 上行、下行拆成两条 PeerConnection：订阅集合变化时只重协商下行，
 * 不会连带把自己的上行也重置掉。
 */
export class MediaEngine {
  private client: Client
  private iceServers: RTCIceServer[]

  private pub: RTCPeerConnection | null = null
  private sub: RTCPeerConnection | null = null

  private localStream: MediaStream | null = null
  private screenStream: MediaStream | null = null

  /** 已收到的远端轨，按 trackId 索引。 */
  private remotes = new Map<string, RemoteTrack>()
  private onRemote: RemoteHandler | null = null
  private onRemoteGone: RemoteGoneHandler | null = null

  private senders = new Map<MediaKind, RTCRtpSender>()

  /** 上行协商锁：发出 offer 的时刻（0 表示空闲），以及期间是否又有变更要补协商。 */
  private pubBusySince = 0
  private pubAgain = false
  private disposers: Array<() => void> = []

  /**
   * 本地流变更回调。切虚拟背景会把摄像头轨换成处理后的那条，
   * 界面必须跟着换 —— 否则出现「对端看到虚化了，自己预览还是原画面」，
   * 用户会以为没生效而反复点。
   */
  private onLocalChange: ((s: MediaStream) => void) | null = null

  /** 虚拟背景：模式与背景图，以及处理句柄。 */
  private bgMode: import('./virtualBgSupport').BgMode = 'off'
  private bgImage: HTMLImageElement | null = null
  private bg: import('./virtualBg').VirtualBgHandle | null = null

  /** 本机说话音量采样器，仅在发布了麦克风时存在。 */
  private meter: SpeakMeter | null = null
  /** 采样器当前绑着的那条轨，换轨后据此判断要不要重建。 */
  private meterTrack: MediaStreamTrack | null = null
  private onSpeak: ((level: number) => void) | null = null

  /**
   * 屏幕共享被浏览器那一侧结束（用户点了浏览器自带的「停止共享」、共享的窗口被关掉）。
   * 媒体层自己只会撤流，不知道还要通知房间「共享结束了」—— 交给上层统一走 stopShare。
   */
  private onScreenEnded: (() => void) | null = null

  /** 已经挂过事件的远端轨。同一个接收器被服务端复用时 ontrack 会再来一次，别重复挂。 */
  private wired = new WeakSet<MediaStreamTrack>()

  constructor(opt: MediaOptions) {
    this.client = opt.client
    this.iceServers = opt.iceServers.map((s) => ({
      urls: s.urls,
      username: s.username,
      credential: s.credential,
    }))
    this.bind()
  }

  /** 注册说话音量回调。传 null 关闭上报。 */
  setSpeakHandler(fn: ((level: number) => void) | null): void {
    this.onSpeak = fn
    this.syncMeter()
  }

  /** 按当前是否有本地音频轨，决定采样器的起停。
   *
   * 每次麦克风轨变动都要调一次：换设备、静音重开、离开房间。
   * 漏掉 stop 会让 AudioContext 一直挂着，而浏览器对同时存在的
   * AudioContext 有数量上限，泄漏几次之后新的音频功能就静默失效了。
   */
  private syncMeter(): void {
    const track = this.localStream?.getAudioTracks()[0]
    if (!track || !this.onSpeak) {
      this.meter?.stop()
      this.meter = null
      this.meterTrack = null
      return
    }
    // 采样器绑的是「某一条轨」而不是流：来电恢复用 replaceTrack 换了新轨之后，
    // 旧采样器还挂在那条已 ended 的轨上，读出来恒为 0 —— 说话指示灯就再也不亮了。
    // 所以轨变了必须重建，只看「采样器在不在」是不够的。
    if (this.meter && this.meterTrack === track) return
    this.meter?.stop()
    this.meter = startSpeakMeter(new MediaStream([track]), (lv) => this.onSpeak?.(lv))
    this.meterTrack = track
  }

  /** 注册远端轨到达/离开的回调。 */
  setHandlers(onTrack: RemoteHandler, onGone: RemoteGoneHandler): void {
    this.onRemote = onTrack
    this.onRemoteGone = onGone
  }

  /** 注册「屏幕共享被浏览器一侧结束」的回调。 */
  setScreenEndedHandler(fn: (() => void) | null): void {
    this.onScreenEnded = fn
  }

  /** 本机是否还挂着屏幕共享的采集。 */
  hasScreen(): boolean {
    return !!this.screenStream
  }

  /**
   * 信令断线重连后重建媒体连接。
   *
   * 服务端在 WebSocket 一断就把这个会话的媒体全部拆了（SFU.Detach），重连上来是
   * 一个全新的 peer：旧的上下行连接两头都已作废，服务端也不知道我在推什么。
   * 不重建的话工具栏照样显示麦克风开着，别人却再也听不到 —— 而且没有任何报错。
   *
   * 本地采集**不**重新 getUserMedia：轨还活着，用户的开关（静音、关摄像头）
   * 都记在轨的 enabled 上，原样加回新连接就等于「按当前开关重新发布」，
   * 也不会再弹一次权限框、摄像头指示灯不会闪。
   *
   * 屏幕共享不自动恢复：服务端在掉线那一刻已经替他结束了共享（广播过 share.stop），
   * 断线期间别人可能已经开始共享；这时悄悄重推只会被拒或抢走别人的共享。
   * 停掉采集、由上层提示他重新发起，和腾讯会议的处理一致。
   *
   * 返回屏幕共享是否因此被停掉，供上层决定要不要提示。
   */
  async rebuildAfterReconnect(): Promise<{ screenDropped: boolean }> {
    const screenDropped = !!this.screenStream
    this.screenStream?.getTracks().forEach((t) => t.stop())
    this.screenStream = null

    // 旧连接作废：两头都关，远端轨表清空（上层的 remoteTracks 由上层自己清）。
    this.pub?.close()
    this.sub?.close()
    this.pub = null
    this.sub = null
    this.pubBusySince = 0
    this.pubAgain = false
    this.remotes.clear()
    this.senders.clear()

    const local = this.localStream
    const tracks = (local?.getTracks() ?? []).filter((t) => t.readyState === 'live')
    if (!local || tracks.length === 0) return { screenDropped }

    const pc = this.ensurePub()
    const added: MediaKind[] = []
    const decls: Array<{ kind: MediaKind; id: string }> = []
    for (const track of tracks) {
      const kind: MediaKind = track.kind === 'audio' ? 'audio' : 'video'
      if (this.senders.has(kind)) continue
      this.senders.set(kind, pc.addTrack(track, local))
      added.push(kind)
      decls.push({ kind, id: track.id })
    }
    this.client.send(MsgType.RTCPublish, { media: added, tracks: decls })
    await this.negotiatePub()
    return { screenDropped }
  }

  /**
   * 开始推送麦克风与（可选）摄像头。
   *
   * 只采集「要开但还没开」的那一路：先开麦再开摄像头时，
   * 若照单全收地重新 getUserMedia，会把已在推的音频再加一条，
   * 对端就会听到两份自己的声音。
   */
  async publish(opts: { audio: boolean; video: boolean }): Promise<MediaStream> {
    const need: MediaStreamConstraints = {}
    if (opts.audio && !this.senders.has('audio')) {
      // 课堂场景必开这三项，缺一项都会让课上不下去：
      //   回声消除  外放时对方会听到自己的声音绕回去，越叠越响直到啸叫
      //   噪声抑制  教室的风扇、空调、翻书声会盖过讲课声
      //   自动增益  学生离麦远近差很多，不拉平就得一直手动调音量
      //
      // 对应老系统 AudioLib 里的 Speex 预处理。核对过老代码：
      // 它实际启用的是 DENOISE + AGC + VAD（IAudioCodec.cpp），
      // 回声消除那套（AudioLib/AEC/，Speex 的 speex_echo）代码在工程里，
      // 但没有任何调用点 —— 也就是说老系统的回声消除并没有真正生效。
      //
      // 这里必须显式写出来，不能依赖浏览器默认值：默认值随浏览器与版本
      // 变化，而且屏幕共享带音频那条路径的默认就是关的。
      need.audio = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }
    }
    if (opts.video && !this.senders.has('video')) {
      need.video = { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 20 } }
    }

    // 需要的都已在推，直接返回现有轨的快照。
    if (!need.audio && !need.video) {
      return this.snapshotLocal()
    }

    const blocked = mediaBlockedReason()
    if (blocked) throw new Error(blocked)
    // 用户在设置里选过设备的话，按选择来；没选过就跟随系统默认。
    let fresh = await navigator.mediaDevices.getUserMedia(applyPreferred(need))

    // 虚拟背景：把摄像头轨换成处理后的轨，推流与预览就都自动带上效果。
    // 放在这里而不是各调用点，是为了让「换设备后重新 publish」也不丢效果。
    if (need.video && this.bgMode !== 'off' && fresh.getVideoTracks().length > 0) {
      try {
        const { attachVirtualBg } = await import('./virtualBg')
        this.bg?.stop()
        this.bg = await attachVirtualBg(fresh, this.bgMode)
        this.bg.setMode(this.bgMode, this.bgImage)
        fresh = this.bg.stream
      } catch (e) {
        // 分割起不来就用原始画面。虚拟背景是增强功能，
        // 不能因为它把摄像头整个搞挂 —— 那比没有虚拟背景严重得多。
        // 必须把真实原因打出来：静默降级的话，表现就是「按钮亮了但画面没变」，
        // 排查时完全没有线索。
        console.warn('[虚拟背景] 启用失败，改用原始画面：', e)
      }
    }

    // 汇总到同一个本地流，便于预览与统一开关。
    if (!this.localStream) this.localStream = new MediaStream()
    const local = this.localStream

    // 加新轨之前先摘掉同类型的旧轨。
    //
    // unpublish 只处理「有 sender 的」轨，而虚拟背景换轨时原始轨已经 stop()
    // 但仍留在 localStream 里。一个流里挂两条视频轨时，<video> 只显示第一条 ——
    // 于是对端看到的是虚化后的画面，自己预览却还是原始画面，
    // 用户会以为功能没生效而反复点开关。
    for (const t of fresh.getTracks()) {
      const same = t.kind === 'audio' ? local.getAudioTracks() : local.getVideoTracks()
      for (const old of same) {
        if (old.id !== t.id) {
          old.stop()
          local.removeTrack(old)
        }
      }
    }

    const pc = this.ensurePub()
    const added: MediaKind[] = []
    const decls: Array<{ kind: MediaKind; id: string }> = []
    for (const track of fresh.getTracks()) {
      const kind: MediaKind = track.kind === 'audio' ? 'audio' : 'video'
      local.addTrack(track)
      this.senders.set(kind, pc.addTrack(track, local))
      added.push(kind)
      decls.push({ kind, id: track.id })
    }
    this.syncMeter()

    if (added.length === 0) {
      // 这条路径也要通知界面：切虚拟背景时轨换了但数量没变，
      // 漏掉通知的话预览还挂着旧轨 —— 对端已经虚化了，自己看还是原画面。
      const snap = this.snapshotLocal()
      this.onLocalChange?.(snap)
      return snap
    }
    // 必须声明每条轨的用途：服务端无法从 SDP 判断一条 video 是摄像头还是屏幕。
    this.client.send(MsgType.RTCPublish, { media: added, tracks: decls })
    await this.negotiatePub()
    const snap = this.snapshotLocal()
    this.onLocalChange?.(snap)
    return snap
  }

  /** 注册本地流变更回调。 */
  setLocalStreamHandler(fn: ((s: MediaStream) => void) | null): void {
    this.onLocalChange = fn
    if (typeof window !== 'undefined') {
      ;(window as unknown as { __vbgHandlerSet?: boolean }).__vbgHandlerSet = !!fn
    }
  }

  /** 当前虚拟背景模式。 */
  backgroundMode(): import('./virtualBgSupport').BgMode {
    return this.bgMode
  }

  /**
   * 切换虚拟背景。
   *
   * 已在推流时必须重新走一遍 publish：处理后的是**另一条轨**，
   * 不换掉 sender 里那条的话，对端看到的还是原始画面 ——
   * 而本地预览却是有效果的，于是「我这边好了，别人说没变」。
   */
  async setBackground(mode: import('./virtualBgSupport').BgMode, img?: HTMLImageElement | null): Promise<void> {
    this.bgMode = mode
    if (img !== undefined) this.bgImage = img
    const hasVideo = (this.localStream?.getVideoTracks().length ?? 0) > 0
    if (!hasVideo) return

    // 只重采视频：publish 内部对已有的 sender 会跳过（见 opts.audio 那个判断），
    // 所以传 audio:false 不会动已开的麦克风。
    if (mode === 'off') {
      this.bg?.stop()
      this.bg = null
      await this.unpublish(['video'])
      await this.publish({ audio: false, video: true })
      return
    }
    if (this.bg) {
      // 已在处理中，只改参数，不重新采流（重采会闪一下黑）。
      // 轨没换，但界面仍要收到一次通知：调用方（设置面板）可能正等着
      // 用新流刷新预览，不通知的话它会一直显示切换前的画面。
      this.bg.setMode(mode, this.bgImage)
      this.onLocalChange?.(this.snapshotLocal())
      return
    }
    await this.unpublish(['video'])
    await this.publish({ audio: false, video: true })
  }

  /**
   * 返回本地轨的一个新 MediaStream 包装。
   *
   * 必须每次返回新对象：界面用它做 React 状态，
   * 若始终是同一个流对象，先开麦后开摄像头时引用没变，
   * 预览窗就不会刷新出画面。
   */
  /**
   * 取本机音频流，供字幕采集用。没有麦克风轨时返回 null。
   *
   * 复用已推流的那条轨而不是重新 getUserMedia —— 重复取流会让某些系统
   * 出现两个「正在使用麦克风」的指示，用户会以为被偷录。
   */
  localAudioStream(): MediaStream | null {
    const tracks = this.localStream?.getAudioTracks() ?? []
    return tracks.length > 0 ? new MediaStream(tracks) : null
  }

  private snapshotLocal(): MediaStream {
    return new MediaStream(this.localStream?.getTracks() ?? [])
  }

  /** 停止推流。 */
  async unpublish(media: MediaKind[]): Promise<void> {
    const pc = this.pub
    for (const kind of media) {
      const sender = this.senders.get(kind)
      if (sender) {
        if (pc) pc.removeTrack(sender)
        // 轨要停掉并从本地流里摘走，否则摄像头指示灯不灭，
        // 下次开启时又会因为「流里还挂着旧轨」而重复添加。
        const track = sender.track
        if (track) {
          track.stop()
          this.localStream?.removeTrack(track)
        }
        this.senders.delete(kind)
      }
      if (kind === 'screen') {
        this.screenStream?.getTracks().forEach((t) => t.stop())
        this.screenStream = null
      }
    }
    this.syncMeter()
    this.client.send(MsgType.RTCUnpublish, { media })
    if (pc) await this.negotiatePub()
  }

  /** 开始屏幕共享。 */
  async publishScreen(): Promise<MediaStream> {
    const blocked = mediaBlockedReason()
    if (blocked) throw new Error(blocked)
    // 手机浏览器和部分内嵌 WebView 没有 getDisplayMedia：直接调会抛一句
    // 「is not a function」，用户看不懂，这里换成人话。
    if (typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
      throw new Error('当前浏览器不支持屏幕共享，请换用电脑上的 Chrome、Edge 或桌面客户端')
    }
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 12 } },
      // 不采系统声音。
      //
      // 采了的话，老师电脑放视频的声音会经这条路送出去，同时又从
      // 麦克风绕回来一遍 —— 而共享音频这条路径上浏览器默认不做回声消除，
      // 结果是对方听到两份、还带回声。要放教学视频，让学生自己开着音箱
      // 听麦克风采到的声音更稳，音质差一点但不会啸叫。
      audio: false,
    })
    // 上一次共享还没收干净（例如被别人停掉而这边漏了收尾）就先撤掉：
    // 直接覆盖 screenStream 的话旧流的采集永远不会 stop，系统的「正在共享」指示一直亮着。
    if (this.screenStream || this.senders.has('screen')) await this.unpublish(['screen'])
    this.screenStream = stream

    const pc = this.ensurePub()
    const decls: Array<{ kind: MediaKind; id: string }> = []
    for (const track of stream.getVideoTracks()) {
      const sender = pc.addTransceiver(track, {
        direction: 'sendonly',
        streams: [stream],
      }).sender
      this.senders.set('screen', sender)
      // 浏览器给显示流的轨只有一个随机 UUID，服务端猜不出用途，
      // 必须由这里显式声明成 screen，否则会被当成摄像头。
      decls.push({ kind: 'screen', id: track.id })

      // 用户点浏览器自带的「停止共享」时要同步通知服务端。
      //
      // 只撤流是不够的：服务端的共享状态靠 share.stop 才会清，漏了它
      // 服务端仍认为他在共享 —— 别人发不起共享、观众停在黑画面、他自己的工具栏
      // 还显示「停止共享」。所以交给上层走和点按钮完全相同的 stopShare。
      // 已经被换掉的旧轨（再次共享时）结束不该把新的共享也停掉。
      track.addEventListener('ended', () => {
        if (this.screenStream !== stream) return
        if (this.onScreenEnded) this.onScreenEnded()
        else void this.unpublish(['screen'])
      })
    }

    this.client.send(MsgType.RTCPublish, { media: ['screen'], tracks: decls })
    await this.negotiatePub()
    return stream
  }

  /** 订阅某用户的媒体。 */
  subscribe(userId: number, media: MediaKind[]): void {
    this.ensureSub()
    this.client.send(MsgType.RTCSubscribe, { userId, media })
  }

  /** 退订某用户的媒体。 */
  unsubscribe(userId: number, media: MediaKind[]): void {
    this.client.send(MsgType.RTCUnsubscribe, { userId, media })
  }

  /**
   * 把被系统掐断的本地轨换回来。手机来电、切后台都会让 track 变成 'ended'，
   * 而 sender 和连接都还在 —— 界面上显示开着，对面什么都收不到。
   * 对每一路 ended 的轨重新 getUserMedia 一路同类型的，replaceTrack 换上。
   * 返回哪几路真的换过了，调用方据此决定要不要提示。
   */
  async recoverLocalTracks(): Promise<{ audio: boolean; video: boolean }> {
    const out = { audio: false, video: false }
    for (const kind of ['audio', 'video'] as const) {
      const sender = this.senders.get(kind)
      const old = sender?.track
      if (!sender || !old || old.readyState !== 'ended') continue
      try {
        const fresh = await navigator.mediaDevices.getUserMedia(
          applyPreferred(kind === 'audio' ? { audio: true, video: false } : { audio: false, video: true }),
        )
        const track = fresh.getTracks()[0]
        if (!track) continue
        // 保留用户原来的静音 / 关摄像头状态
        track.enabled = old.enabled
        await sender.replaceTrack(track)
        this.localStream?.removeTrack(old)
        this.localStream?.addTrack(track)
        out[kind] = true
      } catch {
        // 权限被收回（比如来电期间用户在系统设置里改了）就换不回来，
        // 静默放过：下一次用户手动开麦时会走正常的报错路径。
      }
    }
    if (out.audio) this.syncMeter()
    if ((out.audio || out.video) && this.localStream) this.onLocalChange?.(this.localStream)
    return out
  }

  /**
   * 共享者自己的屏幕画面。
   *
   * 共享的人原本在界面上看不到自己共享了什么 —— 而「共享时批注」的主要用户恰恰是他：
   * 要在画面上圈画，就得先看得到画面。回放本地流而不是等服务端转回来，
   * 既没有额外带宽也没有延迟。
   */
  localScreenStream(): MediaStream | null {
    const tracks = this.screenStream?.getVideoTracks() ?? []
    return tracks.length > 0 ? new MediaStream(tracks) : null
  }

  /** 当前是否静音。所有音轨都被禁用才算静音；没有音轨时按「没静音」算。 */
  isMuted(): boolean {
    const tracks = this.localStream?.getAudioTracks() ?? []
    return tracks.length > 0 && tracks.every((t) => !t.enabled)
  }

  /** 麦克风静音开关。 */
  setMuted(muted: boolean): void {
    this.localStream?.getAudioTracks().forEach((t) => {
      t.enabled = !muted
    })
  }

  /** 摄像头开关。 */
  setCameraOff(off: boolean): void {
    this.localStream?.getVideoTracks().forEach((t) => {
      t.enabled = !off
    })
  }

  /** 屏幕共享暂停开关。
   *
   * 真的把轨道停掉，而不只是通知对方「我暂停了」——
   * 后者只是让观众界面糊上一层，画面照旧在往外推，
   * 切到私密内容一样会被看见，那就失去暂停的意义了。
   */
  setSharePaused(paused: boolean): void {
    this.screenStream?.getVideoTracks().forEach((t) => {
      t.enabled = !paused
    })
  }

  /** 释放全部媒体资源。 */
  dispose(): void {
    this.meter?.stop()
    this.meter = null
    this.meterTrack = null
    this.onScreenEnded = null
    this.disposers.forEach((d) => d())
    this.disposers = []
    this.bg?.stop()
    this.bg = null
    this.localStream?.getTracks().forEach((t) => t.stop())
    this.screenStream?.getTracks().forEach((t) => t.stop())
    this.localStream = null
    this.screenStream = null
    this.pub?.close()
    this.sub?.close()
    this.pub = null
    this.sub = null
    this.remotes.clear()
    this.senders.clear()
  }

  // ---------- 内部实现 ----------

  private bind(): void {
    this.disposers.push(
      this.client.on(MsgType.RTCAnswer, (d) => {
        const p = d as { sdp: string; kind: RTCKind }
        if (p.kind === 'sub') {
          void this.sub?.setRemoteDescription({ type: 'answer', sdp: p.sdp })
          return
        }
        const pc = this.pub
        if (!pc) return
        void pc
          .setRemoteDescription({ type: 'answer', sdp: p.sdp })
          .catch((e) => console.warn('[whatylive] 上行 answer 被拒', e))
          .finally(() => {
            if (pc === this.pub) this.pubSettled()
          })
      }),
    )

    // 服务端在下行轨集合变化时会主动发 offer。
    this.disposers.push(
      this.client.on(MsgType.RTCOffer, async (d) => {
        const p = d as { sdp: string; kind: RTCKind }
        if (p.kind !== 'sub') return
        const pc = this.ensureSub()
        await pc.setRemoteDescription({ type: 'offer', sdp: p.sdp })
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        this.client.send(MsgType.RTCAnswer, { sdp: answer.sdp, kind: 'sub' })
      }),
    )

    this.disposers.push(
      this.client.on(MsgType.RTCCandidate, (d) => {
        const p = d as {
          candidate: string
          sdpMid: string
          sdpMLineIndex: number
          kind: RTCKind
        }
        const pc = p.kind === 'sub' ? this.sub : this.pub
        if (!pc || !p.candidate) return
        void pc
          .addIceCandidate({
            candidate: p.candidate,
            sdpMid: p.sdpMid,
            sdpMLineIndex: p.sdpMLineIndex,
          })
          .catch(() => {
            /* 远端描述未就绪时浏览器会自行缓存，忽略即可 */
          })
      }),
    )
  }

  /**
   * 当前的两条连接，供网络质量采样用。
   *
   * 只读地暴露出去，而不是把采样逻辑塞进媒体层：那是两件事，
   * 混在一起以后想换评估口径就得动推流代码。
   */
  peerConnections(): Array<RTCPeerConnection | null> {
    return [this.pub, this.sub]
  }

  private ensurePub(): RTCPeerConnection {
    if (this.pub) return this.pub
    const pc = new RTCPeerConnection({ iceServers: this.iceServers })
    this.pub = pc
    pc.onicecandidate = (e) => this.sendCandidate(e.candidate, 'pub')
    return pc
  }

  private ensureSub(): RTCPeerConnection {
    if (this.sub) return this.sub
    const pc = new RTCPeerConnection({ iceServers: this.iceServers })
    this.sub = pc
    pc.onicecandidate = (e) => this.sendCandidate(e.candidate, 'sub')
    pc.ontrack = (e) => this.handleRemoteTrack(e)
    return pc
  }

  private handleRemoteTrack(e: RTCTrackEvent): void {
    // 服务端把 trackId 编成 "席位号|媒体种类|原始ID"。
    const raw = e.track.id
    const parts = raw.split('|')
    const userId = Number(parts[0])
    const kind = (parts[1] as MediaKind) ?? (e.track.kind === 'audio' ? 'audio' : 'video')

    // 每条轨单独包一个流，不能用 e.streams[0]。
    //
    // 服务端把同一个人的所有轨都归进同一个流（streamIDOf），于是 e.streams[0]
    // 里同时装着他的摄像头和屏幕：<video> 只播流里第一条视频轨，共享画面那一格
    // 播出来的是摄像头。音频也一样会混进视频元素里，绕过 AudioSink 的扬声器选择。
    const track = e.track
    const stream = new MediaStream([track])
    const rt: RemoteTrack = { userId, kind, trackId: raw, stream }
    this.remotes.set(raw, rt)
    this.onRemote?.(rt)

    if (this.wired.has(track)) return
    this.wired.add(track)

    const gone = () => {
      // 按轨对象核对：重连后同一个 trackId 可能已经是新连接上的另一条轨，
      // 旧轨迟到的 ended 不能把新的删掉。
      const cur = this.remotes.get(raw)
      if (!cur || cur.stream.getTracks()[0] !== track) return
      this.remotes.delete(raw)
      this.onRemoteGone?.(raw)
    }
    // 远端关摄像头、关麦、被退订时，服务端 RemoveTrack 后接收端拿到的只是
    // 流上的 removetrack 和轨上的 mute，track 本身并不 ended。只等 ended 的话
    // 轨一直挂着：宫格里是一块黑画面而不是头像、麦克风图标显示开着，
    // 还白占一个视频订阅名额。
    track.addEventListener('ended', gone)
    track.addEventListener('mute', gone)
    for (const s of e.streams) {
      s.addEventListener('removetrack', (ev) => {
        if ((ev as MediaStreamTrackEvent).track === track) gone()
      })
    }
    // mute 也可能只是一阵没收到包（网络抖动），包回来时会 unmute —— 那就把它放回去，
    // 否则一次抖动就让一个人的画面永久消失。
    track.addEventListener('unmute', () => {
      if (this.remotes.has(raw) || track.readyState === 'ended') return
      this.remotes.set(raw, rt)
      this.onRemote?.(rt)
    })
  }

  private sendCandidate(c: RTCIceCandidate | null, kind: RTCKind): void {
    if (!c) return
    this.client.send(MsgType.RTCCandidate, {
      candidate: c.candidate,
      sdpMid: c.sdpMid ?? '',
      sdpMLineIndex: c.sdpMLineIndex ?? 0,
      kind,
    })
  }

  /**
   * 上行重协商，串行化：上一轮的 answer 回来之前不发下一个 offer，期间的变更攒成一轮补发。
   *
   * 连着点「开启视频」「解除静音」会并发两轮协商：第二个 offer 已经 setLocalDescription 了，
   * 第一个 answer 才回来 —— 浏览器报「m-line 顺序不匹配」「send direction 不兼容」拒收，
   * 那一轮的变更就没生效。
   */
  private async negotiatePub(): Promise<void> {
    const pc = this.pub
    if (!pc) return
    // 等 answer 超过这么久就当丢了（服务端报错不回 answer），别让上行永远卡住。
    if (this.pubBusySince > 0 && Date.now() - this.pubBusySince < PUB_ANSWER_TIMEOUT_MS) {
      this.pubAgain = true
      return
    }
    this.pubBusySince = Date.now()
    this.pubAgain = false
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    this.client.send(MsgType.RTCOffer, { sdp: offer.sdp, kind: 'pub' })
  }

  /** 上行 answer 落定：放开协商锁，有攒下的变更就再来一轮。 */
  private pubSettled(): void {
    this.pubBusySince = 0
    if (this.pubAgain) {
      this.pubAgain = false
      void this.negotiatePub()
    }
  }
}
